import { useCallback, useEffect, useState } from 'react';
import Icon from '../components/Icon';
import SubjectClassGrid, { GridLegend } from '../components/adoption/SubjectClassGrid';
import TitleAdoptions from '../components/adoption/TitleAdoptions';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';

/**
 * Module 4 — which titles each school has taken.
 *
 * <p>Two screens where there was one grid, because there are two questions and a
 * School × Title grid was the wrong shape for both. A title is a subject and a class,
 * so a catalogue can hold subjects × classes of them: nineteen subjects over fifteen
 * classes is 285 columns, 5,700 checkboxes over a page of twenty schools, and a header
 * row nobody can read once they have scrolled past the pinned column.
 *
 * <p><b>By school</b> is the editing surface: one school, a Subject × Class grid. Both
 * axes are short, ordered and named, the catalogue's own sparsity becomes legible, and
 * a subject or a class can be taken in one click — which is how adoption is actually
 * sold.
 *
 * <p><b>By title</b> is the reporting one: one book, a paged list of schools. It
 * carries the city, the rep and the date a cell never could, and it scales to a
 * catalogue of any size.
 */
export default function AdoptionPage() {
  const [tab, setTab] = useState('school');
  const [titles, setTitles] = useState([]);
  const [classOrder, setClassOrder] = useState([]);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // The school being edited, or null while one is being chosen.
  const [school, setSchool] = useState(null);
  const [adoptions, setAdoptions] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadTitles = useCallback(async () => {
    setTitles(await api.listTitles());
  }, []);

  useEffect(() => {
    loadTitles().catch(setError);
    // The class order is the master's, not alphabetical: Nursery, LKG and UKG come
    // before Class 1, and Class 10 comes after Class 9 rather than between 1 and 2.
    api.lookupBookClasses().then(setClassOrder).catch(() => setClassOrder([]));
  }, [loadTitles]);

  const openSchool = useCallback(async (row) => {
    setSchool(row);
    setAdoptions(null);
    setError(null);
    try {
      setAdoptions(await api.schoolAdoptions(row.id));
    } catch (err) {
      setError(err);
    }
  }, []);

  /**
   * Applies a cell, a row or a column, and redraws from the answer.
   *
   * <p>The server sends the school's whole adoption list back, so the grid is never
   * assembled from a dozen separate replies — the one way a bulk toggle could leave
   * the screen disagreeing with the database.
   */
  async function apply(titleIds, adopted) {
    setBusy(true);
    setError(null);
    try {
      setAdoptions(await api.adoptTitles(school.id, titleIds, adopted));
      setToast(
        titleIds.length === 1
          ? adopted
            ? 'Adopted'
            : 'Dropped'
          : `${titleIds.length} titles ${adopted ? 'adopted' : 'dropped'}`,
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>Adoptions</h1>
          <p>
            Which titles each school has taken. A teacher only ever sees resources for
            their school&apos;s adopted titles.
          </p>
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* Two questions, named as questions. "By school" and "By title" say less than
          what each one answers. */}
      <div className="filter-chips" style={{ marginBottom: 'var(--s1)' }} role="tablist">
        <button
          type="button"
          role="tab"
          className="chip"
          aria-pressed={tab === 'school'}
          onClick={() => setTab('school')}
        >
          What has a school taken?
        </button>
        <button
          type="button"
          role="tab"
          className="chip"
          aria-pressed={tab === 'title'}
          onClick={() => setTab('title')}
        >
          Who has taken a book?
        </button>
      </div>

      {tab === 'school' && !school && <SchoolChooser onPick={openSchool} onError={setError} />}

      {tab === 'school' && school && (
        <>
          <div className="page-head row-between" style={{ marginBottom: 'var(--s1)' }}>
            <div>
              <nav className="crumbs" aria-label="Breadcrumb">
                <button type="button" className="link-button" onClick={() => setSchool(null)}>
                  All schools
                </button>
                <span aria-hidden="true"> / </span>
                <span aria-current="page">{school.name}</span>
              </nav>
              <h2 style={{ fontSize: 'var(--fs-lg)' }}>{school.name}</h2>
              <p className="muted small" style={{ margin: '2px 0 0' }}>
                {[school.code, school.city?.name, school.state?.name]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>

          {adoptions && !adoptions.editable && (
            <InfoBanner>
              You can read what this school has taken and not change it. Adoptions are the
              Super Admin&apos;s and the school&apos;s own Sales Rep&apos;s.
            </InfoBanner>
          )}

          <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
            <div className="row-between" style={{ flexWrap: 'wrap', gap: 'var(--s1)' }}>
              <GridLegend />
              <span className="small muted">
                {adoptions
                  ? `${adoptions.titles.filter((title) => title.adopted).length} of ${adoptions.titles.length} adopted`
                  : 'Loading…'}
              </span>
            </div>
          </section>

          <section className="card">
            {adoptions === null ? (
              <div className="empty">Loading…</div>
            ) : (
              <SubjectClassGrid
                data={adoptions}
                classOrder={classOrder}
                busy={busy}
                onApply={apply}
              />
            )}
            {adoptions?.editable && (
              <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)' }}>
                <span className="tiny muted">
                  Click a subject or a class to take or drop the whole line.
                </span>
              </div>
            )}
          </section>
        </>
      )}

      {tab === 'title' && <TitleAdoptions titles={titles} />}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2200)} />
    </>
  );
}

/**
 * Picking the school to edit.
 *
 * <p>A list rather than a typeahead: a Sales Rep has tens of schools and wants to see
 * them, and the search is here for the one who has hundreds. Territory-scoped by the
 * server, so what is listed is what the caller may touch.
 */
function SchoolChooser({ onPick, onError }) {
  const [page, setPage] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (pageNumber = 0) => {
      setLoading(true);
      try {
        setPage(await api.listSchools({ query, page: pageNumber, size: 20 }));
      } catch (err) {
        onError(err);
      } finally {
        setLoading(false);
      }
    },
    [query, onError],
  );

  useEffect(() => {
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  const rows = page?.items ?? [];

  return (
    <section className="card">
      <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)' }}>
        <input
          className="input"
          style={{ flex: '1 1 220px' }}
          placeholder="Search school name, code or city"
          aria-label="Search schools"
          maxLength={100}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>School</th>
              <th>City</th>
              <th>State</th>
              <th>Board</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <button type="button" className="link-button" onClick={() => onPick(row)}>
                    {row.name}
                  </button>
                  <span className="muted small"> · {row.code}</span>
                </td>
                {/* MasterRef, not a string: GET /schools answers with SchoolResponse,
                    where city, state and board are each {id, name}. */}
                <td>{row.city?.name}</td>
                <td>{row.state?.name}</td>
                <td>{row.board?.name}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => onPick(row)}
                  >
                    Adoptions <Icon name="arrow" size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && <div className="empty">Loading…</div>}
      {!loading && rows.length === 0 && <div className="empty">No school matches.</div>}

      <div
        className="toolbar"
        style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}
      >
        <span className="muted small">{page?.totalItems ?? 0} school(s)</span>
        <span style={{ display: 'flex', gap: 'var(--s0)' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={!page || page.page === 0}
            onClick={() => load(page.page - 1)}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={!page?.hasNext}
            onClick={() => load(page.page + 1)}
          >
            Next
          </button>
        </span>
      </div>
    </section>
  );
}
